import type { AnyAction } from '@reduxjs/toolkit';
import { delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import { setFileHasBeenEdited } from './reducer';
import * as FILESTATE_TYPES from './types';
import type { RootState } from 'store';
import { addFileEndingIfMissing, exportFileState } from 'util/file';
import type { CurrentFile } from 'store/storageunits/types';
import {
  StorageUnitStatus,
  StorageUnitType,
  UpdateActionOrigin,
} from 'store/storageunits/types';
import { approveFile, update } from 'api/cloudsave/cloudsaveApi';
import { withRetry } from 'store/sagaUtils';
import { isEditableWithoutUseAuth } from 'util/isEditableHelper';
import { hideNonMajorWarnings } from 'store/units/reducer';
import { setCurrentFile } from 'store/storageunits/reducer';
import { getCurrentFile } from 'store/storageunits/selectors';
import { addMessage } from 'store/snackbar/reducer';
import {
  errorMessage,
  fileApprovedMessage,
} from 'containers/Snackbar/snackbarMessages';
import { storageUnitCache } from 'util/cache/storageUnitCacheIndexedDB';
import keycloak from 'auth/keycloak';
import {
  clearDesignerState,
  editPrintProperties,
} from 'store/settings/reducer';
import { setProjectData } from 'store/documentMetadata/reducer';
import { FILE_CHANGE_ACTIONS } from './fileChangeActions';
import { updateStorageUnitRequest } from 'store/storageunits/sagaActions';
import { buildDesignerStorageUnitUpdate } from 'store/storageunits/buildStorageUnitUpdate';

function* handleFileChange(action: AnyAction) {
  if (action.type === clearDesignerState.type) {
    return;
  }

  const { settingsState, fileState, documentMetadata } = yield select(
    (s: RootState) => s,
  );

  const { autoSave } = settingsState;
  const { fileStatus, fileHasBeenEdited } = fileState;
  const { projectData } = documentMetadata;

  const isFileLoad = action.type === setCurrentFile.type && !fileHasBeenEdited;

  const currentFile: CurrentFile = yield select(getCurrentFile);
  const isApproved = currentFile.status.includes(StorageUnitStatus.APPROVED);

  if (
    isEditableWithoutUseAuth(fileStatus) &&
    // eslint-disable-next-line no-prototype-builtins
    !action.payload?.hasOwnProperty('fileHasBeenEdited') &&
    !isFileLoad &&
    !isApproved
  ) {
    if (projectData?.approvedBy?.length) {
      yield put(hideNonMajorWarnings(false));
    }
    yield put(setFileHasBeenEdited(true));

    if (autoSave) {
      try {
        const autosaveFileId = currentFile.id;
        yield delay(2000);
        const {
          unitConnGroup,
          settingsState: currentSettingsState,
          documentMetadata: currentDocumentMetadata,
          fileState: currentFileState,
        } = yield select((s: RootState) => s);

        const { connectionState, unitState } = unitConnGroup.present;
        const latestCurrentFile: CurrentFile = yield select(getCurrentFile);

        if (
          latestCurrentFile.id !== autosaveFileId ||
          !isEditableWithoutUseAuth(currentFileState.fileStatus)
        ) {
          return;
        }

        // Note: Thumbnail capture needs to be done from React component with access to GoJS diagram
        // For autosave, we'll skip thumbnail updates for now
        const body = buildDesignerStorageUnitUpdate({
          currentFile: latestCurrentFile,
          unitState,
          connectionState,
          settingsState: currentSettingsState,
          documentMetadata: currentDocumentMetadata,
          fileState: currentFileState,
        });
        yield put(updateStorageUnitRequest(body, UpdateActionOrigin.AUTOSAVE));
      } catch (err) {
        Sentry.captureException(err, {
          tags: { saga: 'autosave' },
        });
        yield put(addMessage(errorMessage('Autosave failed')));
      }
    }
  }
}

function* handleApproveFile(
  action: FILESTATE_TYPES.ApproveFileRequestActionInterface,
) {
  try {
    const { id } = action.payload;
    const { settingsState, documentMetadata, fileState } = yield select(
      (state: RootState) => state,
    );
    const { unitState, connectionState } = yield select(
      (state: RootState) => state.unitConnGroup.present,
    );

    const approverName = keycloak.idTokenParsed?.name ?? '';
    const newPrintProperties = {
      ...settingsState.printProperties,
      displayDraft: false,
    };

    const newDocumentMetadata = {
      ...documentMetadata,
      projectData: {
        ...documentMetadata.projectData,
        approvedBy: approverName,
      },
    };

    const newFileState = {
      fileStatus: fileState.fileStatus,
      fileHasBeenEdited: false,
    };

    const body = {
      id,
      type: StorageUnitType.File,
      name: addFileEndingIfMissing(settingsState.fileName),
      fileData: exportFileState({
        unitState,
        connectionState,
        settingState: { ...settingsState, printProperties: newPrintProperties },
        documentMetadata: newDocumentMetadata,
        fileState: newFileState,
        clearApprovedStatus: false,
      }),
      flowsheetMetadata: newDocumentMetadata.flowsheetMetadata,
    };

    // Save file
    yield withRetry(() => update(body));
    // Set status to approved
    yield withRetry(() => approveFile(id));

    const currentFile: CurrentFile = yield select(getCurrentFile);
    const newStatus = currentFile.status.includes(StorageUnitStatus.APPROVED)
      ? currentFile.status
      : [...currentFile.status, StorageUnitStatus.APPROVED];

    if (!currentFile.status.includes(StorageUnitStatus.APPROVED)) {
      yield put(
        setCurrentFile({
          ...currentFile,
          status: newStatus,
        }),
      );
    }

    // Update settings, documentMetadata, and fileState with new values
    yield put(editPrintProperties(newPrintProperties));
    yield put(setProjectData(newDocumentMetadata.projectData));
    yield put(setFileHasBeenEdited(false));

    storageUnitCache
      .getOne(id)
      .then((cachedData) => {
        if (cachedData) {
          void storageUnitCache.setOne({
            ...cachedData,
            fileData: body.fileData,
            status: newStatus,
          });
        }
      })
      .catch(() => {});

    yield put(addMessage(fileApprovedMessage()));
  } catch (err) {
    Sentry.captureException(err, {
      tags: { saga: 'approveFile' },
      extra: { fileId: action.payload.id },
    });
    yield put(addMessage(errorMessage("Couldn't approve file")));
  }
}

function* fileStateSagas() {
  yield takeLatest(
    [...FILE_CHANGE_ACTIONS, clearDesignerState.type],
    handleFileChange,
  );
  yield takeEvery(FILESTATE_TYPES.APPROVE_FILE_REQUEST, handleApproveFile);
}

export default fileStateSagas;
